import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { apiService } from 'src/utils/apiServices';
import {
  PHYSICAL_API,
  authHeaders,
  physicalErrorMessage,
  withoutEmptyValues,
} from 'src/utils/physical-api';

const DeliveryAreaDataContext = createContext({ areas: [], loading: false });
const DeliveryAreaActionsContext = createContext({});

export const DeliveryAreaProvider = ({ children }) => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);

  const getDeliveryAreas = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const query = new URLSearchParams(withoutEmptyValues({ ...filters, take: 100 })).toString();
      const { data } = await apiService.get(`${PHYSICAL_API.deliveryAreas}?${query}`, {
        headers: authHeaders(),
      });
      setAreas(data.data ?? []);
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Could not load delivery areas'));
    } finally {
      setLoading(false);
    }
  }, []);

  const getActiveDeliveryAreas = useCallback(async (destinationDelivery) => {
    try {
      const { data } = await apiService.get(
        `${PHYSICAL_API.deliveryAreas}/active/${destinationDelivery}`,
        { headers: authHeaders() }
      );
      return data ?? [];
    } catch (error) {
      toast.warn(physicalErrorMessage(error, 'Could not load delivery areas'));
      return [];
    }
  }, []);

  const saveDeliveryArea = useCallback(async (id, area) => {
    try {
      const { data } = id
        ? await apiService.patch(`${PHYSICAL_API.deliveryAreas}/${id}`, area, {
            headers: authHeaders(),
          })
        : await apiService.post(PHYSICAL_API.deliveryAreas, area, { headers: authHeaders() });
      toast.success(id ? 'Delivery area updated!' : 'Delivery area created!');
      return data;
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Delivery area not saved'));
      return null;
    }
  }, []);

  const setDeliveryAreaActive = useCallback(async (id, isActive) => {
    try {
      const { data } = await apiService.patch(
        `${PHYSICAL_API.deliveryAreas}/${id}/${isActive ? 'activate' : 'deactivate'}`,
        {},
        { headers: authHeaders() }
      );
      setAreas((prev) => prev.map((area) => (area.id === id ? data : area)));
      return data;
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Status not changed'));
      return null;
    }
  }, []);

  const deleteDeliveryArea = useCallback(async (id) => {
    try {
      await apiService.delete(`${PHYSICAL_API.deliveryAreas}/${id}`, { headers: authHeaders() });
      setAreas((prev) => prev.filter((area) => area.id !== id));
      toast.success('Delivery area deleted!');
      return true;
    } catch (error) {
      toast.error(physicalErrorMessage(error, 'Delivery area not deleted'));
      return false;
    }
  }, []);

  const data = useMemo(() => ({ areas, loading }), [areas, loading]);
  const actions = useMemo(
    () => ({
      getDeliveryAreas,
      getActiveDeliveryAreas,
      saveDeliveryArea,
      setDeliveryAreaActive,
      deleteDeliveryArea,
    }),
    [
      getDeliveryAreas,
      getActiveDeliveryAreas,
      saveDeliveryArea,
      setDeliveryAreaActive,
      deleteDeliveryArea,
    ]
  );

  return (
    <DeliveryAreaDataContext.Provider value={data}>
      <DeliveryAreaActionsContext.Provider value={actions}>
        {children}
      </DeliveryAreaActionsContext.Provider>
    </DeliveryAreaDataContext.Provider>
  );
};

DeliveryAreaProvider.propTypes = {
  children: PropTypes.node,
};

export const useDeliveryAreaData = () => useContext(DeliveryAreaDataContext);
export const useDeliveryAreaActions = () => useContext(DeliveryAreaActionsContext);
